// Icon set — ported verbatim from design/components.jsx.
// Stroke-based, hairline, 14px default. The `I` object exports each named icon.
import type { SVGProps } from "react";

type IconProps = Omit<SVGProps<SVGSVGElement>, "stroke" | "fill"> & {
  d?: string;
  size?: number;
  /** SVG stroke-width override. */
  stroke?: number;
  fill?: string;
};

function Icon({
  d,
  fill,
  size = 14,
  stroke = 1.5,
  ...rest
}: IconProps) {
  return (
    <svg
      className="ic"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill || "none"}
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      {d ? <path d={d} /> : null}
    </svg>
  );
}

const makeIcon = (d: string) =>
  function NamedIcon(p: IconProps) {
    return <Icon {...p} d={d} />;
  };

export const I = {
  Dashboard: makeIcon("M3 13h7V3H3v10zm0 8h7v-6H3v6zm11 0h7V11h-7v10zm0-18v6h7V3h-7z"),
  Merchants: makeIcon("M3 7l9-4 9 4-9 4-9-4zM3 12l9 4 9-4M3 17l9 4 9-4"),
  Payouts: makeIcon("M3 7h14m0 0l-4-4m4 4l-4 4M21 17H7m0 0l4 4m-4-4l4-4"),
  Transactions: makeIcon("M4 6h16M4 12h16M4 18h10"),
  Banks: makeIcon("M3 21h18M5 21V10M19 21V10M9 21v-7M15 21v-7M3 10l9-6 9 6"),
  Keys: makeIcon("M21 2l-2 2m0 0l-7 7a5 5 0 11-1-1l7-7m1 1l3 3-4 1-1-3"),
  Users: makeIcon(
    "M17 21v-2a4 4 0 00-4-4H7a4 4 0 00-4 4v2M11 11a4 4 0 100-8 4 4 0 000 8zm10 10v-2a4 4 0 00-3-3.87M15 3.13a4 4 0 010 7.75",
  ),
  Settings: makeIcon(
    "M12 15a3 3 0 100-6 3 3 0 000 6zm7.4-3a7.4 7.4 0 00-.1-1.4l2-1.5-2-3.4-2.3.9a7.5 7.5 0 00-2.4-1.4L14.2 2H9.8l-.4 2.2A7.5 7.5 0 007 5.6l-2.3-.9-2 3.4 2 1.5a7.4 7.4 0 000 2.8l-2 1.5 2 3.4 2.3-.9a7.5 7.5 0 002.4 1.4l.4 2.2h4.4l.4-2.2a7.5 7.5 0 002.4-1.4l2.3.9 2-3.4-2-1.5c.06-.46.1-.93.1-1.4z",
  ),
  Search: makeIcon("M11 19a8 8 0 100-16 8 8 0 000 16zm10 2l-4.35-4.35"),
  Bell: makeIcon("M6 8a6 6 0 1112 0c0 7 3 7 3 9H3c0-2 3-2 3-9zm5 13a2 2 0 003.5 0"),
  Plus: makeIcon("M12 5v14M5 12h14"),
  Check: makeIcon("M5 13l4 4L19 7"),
  X: makeIcon("M18 6L6 18M6 6l12 12"),
  ChevronDown: makeIcon("M6 9l6 6 6-6"),
  ChevronRight: makeIcon("M9 6l6 6-6 6"),
  ChevronLeft: makeIcon("M15 6l-6 6 6 6"),
  ArrowRight: makeIcon("M5 12h14M13 5l7 7-7 7"),
  ArrowUpRight: makeIcon("M7 17L17 7M8 7h9v9"),
  ArrowDown: makeIcon("M12 5v14M5 12l7 7 7-7"),
  ArrowUp: makeIcon("M12 19V5M5 12l7-7 7 7"),
  Filter: makeIcon("M3 5h18l-7 9v6l-4-2v-4L3 5z"),
  Download: makeIcon("M12 4v12m0 0l-4-4m4 4l4-4M4 20h16"),
  Upload: makeIcon("M12 20V8m0 0l-4 4m4-4l4 4M4 4h16"),
  Mail: makeIcon("M3 6l9 7 9-7M3 6h18v12H3V6z"),
  Lock: makeIcon("M6 11V8a6 6 0 1112 0v3M5 11h14v10H5V11z"),
  Copy: makeIcon("M8 8h11v13H8V8zM5 5h11v3M5 5v11h3"),
  Eye: makeIcon("M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12zm11 3a3 3 0 100-6 3 3 0 000 6z"),
  EyeOff: makeIcon(
    "M3 3l18 18M10.6 6.1A10.6 10.6 0 0112 6c7 0 11 8 11 8a18.7 18.7 0 01-2.4 3.4M7.8 7.8C3.6 9.7 1 12 1 12s4 8 11 8c2.1 0 3.9-.6 5.4-1.4M9.9 9.9a3 3 0 004.2 4.2",
  ),
  Refresh: makeIcon("M21 12a9 9 0 11-3-6.7L21 8m0-5v5h-5"),
  More: makeIcon("M5 12h.01M12 12h.01M19 12h.01"),
  CSV: makeIcon("M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8l-5-5zM14 3v5h5M8 13h8M8 17h5"),
  Wallet: makeIcon("M3 7v12a2 2 0 002 2h14a2 2 0 002-2v-7H6a2 2 0 010-4h15V6a2 2 0 00-2-2H5a2 2 0 00-2 3z"),
  Trash: makeIcon("M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6"),
  Edit: makeIcon("M11 4H4v16h16v-7M18.5 2.5a2.1 2.1 0 013 3L12 15l-4 1 1-4 9.5-9.5z"),
  Calendar: makeIcon("M3 7h18v13H3V7zM3 7V5a2 2 0 012-2h14a2 2 0 012 2v2M8 3v4M16 3v4M3 11h18"),
  Building: makeIcon(
    "M3 21h18M6 21V5a2 2 0 012-2h8a2 2 0 012 2v16M10 9h1M13 9h1M10 13h1M13 13h1M10 17h1M13 17h1",
  ),
  Globe: makeIcon(
    "M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10zm0-20v20M2 12h20M5 5c3 3 11 3 14 0M5 19c3-3 11-3 14 0",
  ),
  Bolt: makeIcon("M13 2L4 14h7l-1 8 9-12h-7l1-8z"),
  Shield: makeIcon("M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"),
  ClipboardCheck: makeIcon(
    "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 012-2h2a2 2 0 012 2v0a2 2 0 01-2 2h-2a2 2 0 01-2-2v0zM9 14l2 2 4-4",
  ),
  AlertCircle: makeIcon("M12 22a10 10 0 100-20 10 10 0 000 20zm0-14v4m0 4h.01"),
  Info: makeIcon("M12 22a10 10 0 100-20 10 10 0 000 20zM12 8h.01M11 12h1v4"),
  Logo: makeIcon("M4 6l8-4 8 4-8 4-8-4zm0 6l8 4 8-4M4 18l8 4 8-4"),
} as const;
